/** Herauthenticatie van de huidige sessie voor gevoelige accountacties. */
import {
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import {
  assertEmailPasswordPresent,
  normalizeEmail,
} from '@/services/authServiceEmailAuth';

/**
 * @typedef {'google' | 'password' | 'anonymous'} ReauthSignInMethod
 */

/**
 * @typedef {object} ReauthenticateUserResult
 * @property {boolean} skipped — true when reauth is not applicable (guest accounts).
 * @property {'anonymous'} [reason] — set when skipped is true for anonymous users.
 * @property {import('firebase/auth').User} [user] — set when reauth succeeded.
 */

/**
 * Refreshes the Firebase session for sensitive actions (e.g. account deletion).
 * Password accounts need `password` or `getPassword(email)`; Google uses a popup.
 * Anonymous accounts skip reauth and return `{ skipped: true }`.
 *
 * @param {ReauthSignInMethod} method — from `resolveAccountSignInMethod` in settings.
 * @param {{ password?: string, getPassword?: (email: string) => Promise<string> }} [options]
 * @returns {Promise<ReauthenticateUserResult>}
 */
export const reauthenticateUser = async (method, options = {}) => {
  if (method === 'anonymous') {
    return { skipped: true, reason: 'anonymous' };
  }

  const user = auth.currentUser;
  if (!user) {
    const err = new Error('No authenticated user');
    err.code = 'auth/no-user';
    throw err;
  }

  try {
    if (method === 'password') {
      const email = normalizeEmail(user.email);
      if (!email) {
        const err = new Error('No email on account');
        err.code = 'auth/missing-email';
        throw err;
      }
      let password = options.password;
      if (password === undefined || password === null) {
        if (typeof options.getPassword !== 'function') {
          const err = new Error('Password required for reauthentication');
          err.code = 'auth/empty-password';
          throw err;
        }
        password = await options.getPassword(email);
      }
      assertEmailPasswordPresent(email, password);
      const credential = EmailAuthProvider.credential(email, password);
      const result = await reauthenticateWithCredential(user, credential);
      return { skipped: false, user: result.user };
    }

    if (method === 'google') {
      const provider = new GoogleAuthProvider();
      const result = await reauthenticateWithPopup(user, provider);
      return { skipped: false, user: result.user };
    }

    const err = new Error('Unsupported sign-in method for reauthentication');
    err.code = 'auth/unsupported-method';
    throw err;
  } catch (error) {
    console.error('AuthService: Reauthentication failed:', error.code ?? error.message);
    throw error;
  }
};
